import React, { useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';

const { Title } = Typography;
const { TextArea } = Input;

interface RevenueServiceTypeRow {
  id: string;
  service_code: string;
  service_name: string;
  description?: string | null;
  revenue_account_id: string;
  is_active: boolean;
  work_order_flow?: boolean;
  revenueAccount?: { id: string; account_code: string; account_name: string };
}

interface CoaLeaf {
  id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  is_group: boolean;
}

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

const QUERY_KEY = ['sales-revenue-service-types', 'manage'];

const RevenueServiceTypeMasterPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<RevenueServiceTypeRow | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=false');
      const list = unwrap<RevenueServiceTypeRow[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts-revenue-rst'],
    queryFn: async () => {
      const res = await api.get<CoaLeaf[]>('/finance/chart-of-accounts');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as CoaLeaf[];
    },
  });

  const incomeLeafOptions = useMemo(() => {
    return coaList
      .filter((a) => !a.is_group && String(a.account_type) === 'Income')
      .map((a) => ({ value: a.id, label: `${a.account_code} — ${a.account_name}` }));
  }, [coaList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, work_order_flow: true });
    setOpen(true);
  };

  const openEdit = (row: RevenueServiceTypeRow) => {
    setEditing(row);
    form.setFieldsValue({
      service_code: row.service_code,
      service_name: row.service_name,
      description: row.description ?? undefined,
      revenue_account_id: row.revenue_account_id,
      is_active: row.is_active,
      work_order_flow: row.work_order_flow !== false,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await api.put(`/sales/revenue-service-types/${editing.id}`, values);
        message.success('Revenue service type updated');
      } else {
        await api.post('/sales/revenue-service-types', values);
        message.success('Revenue service type created');
      }
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['sales-revenue-service-types'] });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/sales/revenue-service-types/${id}`);
      message.success('Revenue service type deleted');
      await queryClient.invalidateQueries({ queryKey: ['sales-revenue-service-types'] });
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<RevenueServiceTypeRow> = [
    { title: 'Code', dataIndex: 'service_code', key: 'service_code', width: 120 },
    { title: 'Service name', dataIndex: 'service_name', key: 'service_name' },
    {
      title: 'Revenue GL',
      key: 'gl',
      render: (_, row) => {
        const ra = row.revenueAccount;
        return ra ? `${ra.account_code} — ${ra.account_name}` : row.revenue_account_id;
      },
    },
    {
      title: 'WO flow',
      dataIndex: 'work_order_flow',
      key: 'work_order_flow',
      width: 80,
      render: (v: boolean | undefined) => (v !== false ? 'Yes' : 'No'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v: boolean) => (v ? 'Active' : 'Inactive'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this revenue service type?"
            onConfirm={() => handleDelete(row.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          Revenue service types
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add service type
        </Button>
      </div>

      <Card>
        <p style={{ marginTop: 0, color: 'rgba(0,0,0,0.55)' }}>
          Define services your company sells and the Income (revenue) GL account used when posting tax invoices. This
          flows from leads and opportunities through quotations and customer orders to invoice GL credits.
        </p>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={isLoading} pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} />
      </Card>

      <Modal
        title={editing ? 'Edit revenue service type' : 'New revenue service type'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="service_code"
            label="Service code"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. INSTALL" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="service_name" label="Service name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Installation services" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item
            name="revenue_account_id"
            label="Revenue (Income) GL account"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select posting Income account"
              options={incomeLeafOptions}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="work_order_flow"
            label="Work order flow"
            valuePropName="checked"
            extra="When on, customer orders using this type must ship via work order / IRN. When off, direct delivery from the customer order is allowed (stock items require sufficient on-hand)."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RevenueServiceTypeMasterPage;
